import type { ReactNode } from 'react';
import { Button } from './ui.js';

/** Header bar for a card-wrapped table, with an optional "+ Add" action. */
export function SectionBar({
  title,
  action,
  onAdd,
  addLabel = '+ Add',
}: {
  title: ReactNode;
  action?: ReactNode;
  onAdd?: () => void;
  addLabel?: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-hv-border px-4 py-3">
      <div className="text-hv-sm font-medium text-hv-text">{title}</div>
      {action ?? (onAdd ? (
        <Button variant="secondary" onClick={onAdd}>
          {addLabel}
        </Button>
      ) : null)}
    </div>
  );
}

export function Tr({ children, head }: { children: ReactNode; head?: boolean }): JSX.Element {
  return <tr className={head ? '' : 'border-t border-hv-border'}>{children}</tr>;
}

export function Th({
  children,
  right,
  center,
}: {
  children?: ReactNode;
  right?: boolean;
  center?: boolean;
}): JSX.Element {
  return (
    <th
      className={`px-3 py-2 text-hv-xs font-medium uppercase tracking-wide text-hv-text-tertiary ${
        right ? 'text-right' : center ? 'text-center' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  right,
  center,
  colSpan,
}: {
  children?: ReactNode;
  right?: boolean;
  center?: boolean;
  colSpan?: number;
}): JSX.Element {
  return (
    <td colSpan={colSpan} className={`px-3 py-1.5 align-middle ${right ? 'text-right' : center ? 'text-center' : ''}`}>
      {children}
    </td>
  );
}

/** A right-aligned, muted, tabular numeric cell for derived/read-only values. */
export function TdNum({ children, strong }: { children: ReactNode; strong?: boolean }): JSX.Element {
  return (
    <td className={`px-3 py-1.5 text-right tabular-nums ${strong ? 'font-medium text-hv-text' : 'text-hv-text-secondary'}`}>
      {children}
    </td>
  );
}
